import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { EnvelopeDetailsSkeleton } from "@/components/skeletons";
import { NO_DOCUMENTS_FOUND, NO_RECIPIENT_FOUND } from "@/utils/constants/messages";
import { useFeaturePermission } from "@/utils/hooks/useFeaturePermission";
import StatusBadge from "@/components/badges/StatusBadge";
import EnvelopRecipientCard from "@/components/cards/EnvelopeRecipientCard";
import ListCard from "@/components/cards/ListCard";
import DocumentModal from "@/components/modals/DocumentModal";
import { getEnvelopeById } from "@/services/envelope.service";
import { DateFormatter, formatPersonWithEmail, TimeFormatter } from "@/utils/functions/helperFunctions";
import { FEATURE_PERMISSIONS } from "@/utils/constants/featurePermissions";

const EnvelopeDetails = () => {
    const [envelopeDetails, setEnvelopeDetails] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const [openDocModal, setOpenDocModal] = useState(false);
    const [documentContent, setDocumentContent] = useState<any>(null);
    const isViewDocumentPermitted = useFeaturePermission(FEATURE_PERMISSIONS.AdminPortalViewDocument);
    const params = useParams();
    const id = params.id || "";

    useEffect(() => {
        if (!id) {
            return;
        }

        setLoading(true);
        getEnvelopeById(
            id,
            (response: any) => {
                setEnvelopeDetails(response.data);
                setLoading(false);
            },
            (error: any) => {
                setLoading(false);
                console.log(error);
            }
        );
    }, [id]);

    const handleOpenDocument = (document: { title: string; sas_url?: string }) => {
        if (!isViewDocumentPermitted || !document.sas_url) return;
        setDocumentContent({ title: document.title, content: document.sas_url });
        setOpenDocModal(true);
    };

    if (loading) {
        return <EnvelopeDetailsSkeleton />;
    }

    const recipients = envelopeDetails?.recipients
        ? [...envelopeDetails.recipients].sort(
            (a: { priority: number }, b: { priority: number }) => a.priority - b.priority
        )
        : [];

    return (
        <div className="gs-envelope-detail-page">
            <div className="gs-envelope-detail-header">
                <h3 className="gs-detail-title" id="envelope-detail-subject">
                    {envelopeDetails?.meta_data?.email_subject}
                </h3>
            </div>

            <div className="gs-list-page-card gs-envelope-detail-card">
                <ul className="m-0 list-none p-0">
                    <ListCard id="envelope-detail-id" label="Envelope ID" value={envelopeDetails?.id} />
                    <ListCard
                        id="envelope-detail-status"
                        label="Status"
                        value={<StatusBadge id="envelope-detail" envelope={envelopeDetails} title="details" />}
                    />
                    <ListCard
                        id="envelope-detail-from"
                        label="From"
                        value={formatPersonWithEmail(envelopeDetails?.created_by)}
                    />
                    <ListCard
                        id="envelope-detail-owner"
                        label="Owner"
                        value={formatPersonWithEmail(envelopeDetails?.owner)}
                    />
                    <ListCard
                        id="envelope-detail-created-at"
                        label="Created at"
                        value={`${DateFormatter(envelopeDetails?.created_at)} | ${TimeFormatter(envelopeDetails?.created_at)}`}
                    />
                    {envelopeDetails?.sent_at && envelopeDetails?.status !== "draft" ? (
                        <ListCard
                            id="envelope-detail-sent-at"
                            label="Sent at"
                            value={`${DateFormatter(envelopeDetails.sent_at)} | ${TimeFormatter(envelopeDetails.sent_at)}`}
                        />
                    ) : null}
                    <ListCard
                        id="envelope-detail-changed-at"
                        label="Last changed at"
                        value={`${DateFormatter(envelopeDetails?.updated_at)} | ${TimeFormatter(envelopeDetails.updated_at)}`}
                    />
                    <ListCard
                        id="envelope-detail-expired-at"
                        label="Expired at"
                        value={`${DateFormatter(envelopeDetails?.expiry_at)} | ${TimeFormatter(envelopeDetails.expiry_at)}`}
                        valueClass="text-danger"
                    />
                </ul>
            </div>

            <h5 className="gs-detail-section-title" id="envelope-detail-recipients-title">
                Recipients
            </h5>
            <div className="gs-list-page-card gs-envelope-detail-card">
                <EnvelopRecipientCard
                    recipients={recipients}
                    emptyMessage={NO_RECIPIENT_FOUND}
                    emptyClassName="gs-detail-empty"
                />
            </div>

            <h5 className="gs-detail-section-title" id="document-card-title">
                Documents
            </h5>
            <div className="gs-list-page-card gs-envelope-detail-card">
                <ul className="m-0 list-none p-0">
                    {envelopeDetails?.documents?.length > 0 ? (
                        envelopeDetails.documents.map((document: any, key: number) => (
                            <li className="gs-envelope-detail-document-item" key={document.id ?? key}>
                                <div className="gs-envelope-detail-document-icon">
                                    <em className="icon ni ni-file-pdf" id={`envelope-detail-document-icon-${key}`} />
                                </div>
                                {isViewDocumentPermitted ? (
                                    <button
                                        type="button"
                                        onClick={() => handleOpenDocument(document)}
                                        className="gs-envelope-detail-document-title cursor-pointer hover:underline"
                                        id={`envelope-detail-document-title-${key}`}
                                    >
                                        {document.title}
                                    </button>
                                ) : (
                                    <div
                                        className="gs-envelope-detail-document-title"
                                        id={`envelope-detail-document-title-${key}`}
                                    >
                                        {document.title}
                                    </div>
                                )}
                            </li>
                        ))
                    ) : (
                        <li className="gs-detail-empty" id="envelope-detail-no-doc-found-message">
                            {NO_DOCUMENTS_FOUND}
                        </li>
                    )}
                </ul>
            </div>

            {isViewDocumentPermitted ? (
                <DocumentModal
                    content={documentContent}
                    show={openDocModal}
                    onHide={() => setOpenDocModal(false)}
                />
            ) : null}
        </div>
    );
};

export default EnvelopeDetails;
